import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Heading,
  Textarea,
  Select,
  Flex,
  FormErrorMessage,
  Link,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from '@chakra-ui/react';
import FileUpload from '../FileUpload';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// Import addDays from date-fns
import { isFuture, isToday, addDays } from 'date-fns';


function HouseInfoStep({ formData, onNext, onBack, onUpdateStepData }) { // Added onUpdateStepData prop
  const [stepData, setStepData] = useState({
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    altitude: '',
    projectType: '',
    projectGoal: '',
    installationTimeline: null,
    homePictures: [],
    electricalPanelPicture: null,
  });
  const [errors, setErrors] = useState({});

  const projectTypes = [
    { value: '', label: 'Select Project Type' },
    { value: 'single_family_retrofit', label: 'Single Family Home Retrofit' },
    { value: 'new_construction', label: 'New Construction' },
    { value: 'multi_family', label: 'Multi-Family' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'other', label: 'Other' },
  ];

  const countryOptions = [
    { value: '', label: 'Select Country' },
    { value: 'USA', label: 'United States' },
    { value: 'CAN', label: 'Canada' },
    { value: 'MEX', label: 'Mexico' },
    { value: 'GBR', label: 'United Kingdom' },
    { value: 'AUS', label: 'Australia' },
    { value: 'OTH', label: 'Other' },
    // Add more countries here
  ];

  // Calculate the minimum selectable date (today + 30 days)
  const minSelectableDate = addDays(new Date(), 30);
  // Set hours, minutes, seconds, milliseconds to 0 for comparison purposes
  minSelectableDate.setHours(0, 0, 0, 0);


  useEffect(() => {
    setStepData(prevData => {
      // Preserve existing formData values; only set defaults if not present
      const initialData = {
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
        altitude: '',
        projectType: '',
        projectGoal: '',
        installationTimeline: null,
        // Only set homePictures to [] if not provided in formData
        homePictures: Array.isArray(formData.homePictures)
          ? formData.homePictures
          : [],
        // Only set electricalPanelPicture to null if not provided in formData
        electricalPanelPicture:
          formData.electricalPanelPicture !== undefined
            ? formData.electricalPanelPicture
            : null,
        ...formData,
      };

      // Ensure uploaded media are correctly formatted arrays
      if (!Array.isArray(initialData.homePictures)) {
        initialData.homePictures = [];
      }

      if (
        formData.installationTimeline &&
        typeof formData.installationTimeline === 'string'
      ) {
        const parsedDate = new Date(formData.installationTimeline);
        initialData.installationTimeline = isNaN(parsedDate.getTime())
          ? null
          : parsedDate;
      }

      return initialData;
    });
  }, [formData]);

  // Handle date change specifically
  const handleDateChange = (date) => {
    const updatedData = { ...stepData, installationTimeline: date };
    setStepData(updatedData);
    onUpdateStepData(updatedData); // Use the update function

     if (errors.installationTimeline) {
      setErrors({ ...errors, installationTimeline: null });
    }
  };

  // Keep handleChange for other input fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...stepData, [name]: value };
    setStepData(updatedData);
    onUpdateStepData(updatedData); // Use the update function

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleFileUpload = (fieldName, files) => {
    const updatedData = { ...stepData, [fieldName]: files };
    setStepData(updatedData);
    onUpdateStepData(updatedData); // Use the update function

    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!stepData.streetAddress) newErrors.streetAddress = 'Street Address is required';
    if (!stepData.city) newErrors.city = 'City is required';
    if (!stepData.state) newErrors.state = 'State is required';
    if (!stepData.zipCode) newErrors.zipCode = 'Zip Code is required';
    if (!stepData.country) newErrors.country = 'Country is required';
    if (!stepData.altitude) newErrors.altitude = 'Altitude is required';
    if (!stepData.projectType) newErrors.projectType = 'Type of Project is required';
    if (!stepData.projectGoal) newErrors.projectGoal = 'Project Goal is required';
    if (!stepData.installationTimeline) {
      newErrors.installationTimeline = 'Installation Timeline is required';
    } else if (stepData.installationTimeline < minSelectableDate) {
      newErrors.installationTimeline = `Please select a date at least ${addDays(new Date(), 30).toLocaleDateString()} or later`;
    }
    if (!stepData.homePictures || stepData.homePictures.length === 0) newErrors.homePictures = 'Pictures of Home are required';
    if (!stepData.electricalPanelPicture) newErrors.electricalPanelPicture = 'Picture of electrical panel is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = () => {
    if (validate()) {
      // Data is already updated via handleChange/handleFileUpload
      onNext(); // Trigger navigation
    }
  };


  return (
    <Box>
      <Heading as="h2" size="lg" mb={6}>
        General House Information
      </Heading>
      <VStack spacing={4}>
        <FormControl id="streetAddress" isRequired isInvalid={!!errors.streetAddress}>
          <FormLabel>Street Address</FormLabel>
          <Input
            name="streetAddress"
            value={stepData.streetAddress || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.streetAddress}</FormErrorMessage>
        </FormControl>

        <FormControl id="city" isRequired isInvalid={!!errors.city}>
          <FormLabel>City</FormLabel>
          <Input
            name="city"
            value={stepData.city || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.city}</FormErrorMessage>
        </FormControl>

        <FormControl id="state" isRequired isInvalid={!!errors.state}>
          <FormLabel>State</FormLabel>
          <Input
            name="state"
            value={stepData.state || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.state}</FormErrorMessage>
        </FormControl>

        <FormControl id="zipCode" isRequired isInvalid={!!errors.zipCode}>
          <FormLabel>Zip Code</FormLabel>
          <Input
            name="zipCode"
            value={stepData.zipCode || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.zipCode}</FormErrorMessage>
        </FormControl>

         <FormControl id="country" isRequired isInvalid={!!errors.country}>
          <FormLabel>Country</FormLabel>
          <Select
            name="country"
            value={stepData.country || ''}
            onChange={handleChange}
            placeholder="Select Country"
          >
            {countryOptions.map(option => (
               option.value !== '' && (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              )
            ))}
          </Select>
          <FormErrorMessage>{errors.country}</FormErrorMessage>
        </FormControl>


        <FormControl id="altitude" isRequired isInvalid={!!errors.altitude}>
          <Popover trigger="hover" placement="right">
            <PopoverTrigger>
               <FormLabel cursor="help">
                 Altitude at Address (in feet)
               </FormLabel>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverBody>
                <Text>
                  If you don't know your altitude, you can look it up here:{' '}
                  <Link href="https://whatismyelevation.com/" isExternal color="teal.500">
                    whatismyelevation.com
                  </Link>
                </Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <Input
            name="altitude"
            value={stepData.altitude || ''}
            onChange={handleChange}
            type="number"
          />
          <FormErrorMessage>{errors.altitude}</FormErrorMessage>
        </FormControl>

        <FormControl id="projectType" isRequired isInvalid={!!errors.projectType}>
          <FormLabel>Type of Project</FormLabel>
          <Select
            name="projectType"
            value={stepData.projectType || ''}
            onChange={handleChange}
          >
            {projectTypes.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.projectType}</FormErrorMessage>
        </FormControl>

        <FormControl id="projectGoal" isRequired isInvalid={!!errors.projectGoal}>
          <FormLabel>What are you hoping to achieve with an altitude simulation/oxygenation system?</FormLabel>
          <Textarea
            name="projectGoal"
            value={stepData.projectGoal || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.projectGoal}</FormErrorMessage>
        </FormControl>

        {/* Installation Timeline Date Picker */}
        <FormControl id="installationTimeline" isRequired isInvalid={!!errors.installationTimeline}>
          <FormLabel>Approximately when would you want your system installed?</FormLabel>
          <DatePicker
            selected={stepData.installationTimeline}
            onChange={handleDateChange}
            dateFormat="MM/dd/yyyy"
            minDate={minSelectableDate} // Set the minimum date to 30 days from now
            placeholderText="Select a date" // Update placeholder text
            customInput={<Input />}
          />
          <FormErrorMessage>{errors.installationTimeline}</FormErrorMessage>
        </FormControl>

        <FormControl id="homePictures" isRequired isInvalid={!!errors.homePictures}>
          <FormLabel>Pictures of Home exterior (front, sides, rear)</FormLabel>
          <FileUpload
            fieldName="homePictures"
            onFilesUpload={(keys) => handleFileUpload('homePictures', keys)}
            allowMultiple={true}
            accept="image/*" // Pass accept prop
            submissionId={formData.submissionId}
          />
           <FormErrorMessage>{errors.homePictures}</FormErrorMessage>
        </FormControl>

        <FormControl id="electricalPanelPicture" isRequired isInvalid={!!errors.electricalPanelPicture}>
          <FormLabel>Picture of electrical panel with door/cover open</FormLabel>
          <FileUpload
            fieldName="electricalPanelPicture"
            onFilesUpload={(key) => handleFileUpload('electricalPanelPicture', key)}
            allowMultiple={false}
            accept="image/*" // Pass accept prop
            submissionId={formData.submissionId}
          />
           <FormErrorMessage>{errors.electricalPanelPicture}</FormErrorMessage>
        </FormControl>

        {/* Navigation buttons for House Info Step */}
        <Flex width="100%" justify="space-between">
           <Button onClick={onBack} variant="outline">
            Back
          </Button>
          <Button colorScheme="brand" onClick={handleNextClick}>
            Next
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
}

export default HouseInfoStep;
